import type { ReactNode } from "react";

import { Trans } from "@lingui/react/macro";
import { cn } from "@pontimed/ui";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, Loader2, Lock, RotateCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { buttonVariants } from "@/components/ui/button-variants";

interface FacilityStateCardProps {
  /** `no-access`: the API answered 403/404. `load-failed`: anything else
   *  (offline, 5xx) — retryable, never shown as "no access". */
  kind: "no-access" | "load-failed";
  facilityId: string;
  /** Re-runs the failed load (load-failed only). */
  onRetry?: () => void;
  retrying?: boolean;
  /** Set on the clinic dashboard itself so the card doesn't link to the
   *  page it is on. */
  onDashboard?: boolean;
  /** Optional sentence under the title (what failed). Never an id. */
  message?: ReactNode;
  className?: string;
}

/**
 * The one "can't show this page" card for facility screens (P16). Replaces
 * 15 NoAccessCard copies that read every failure as "No access" and sent
 * clinic staff to the patient dashboard.
 */
export function FacilityStateCard({
  kind,
  facilityId,
  onRetry,
  retrying = false,
  onDashboard = false,
  message,
  className,
}: FacilityStateCardProps) {
  const failed = kind === "load-failed";
  const Icon = failed ? AlertTriangle : Lock;
  return (
    <div
      role="alert"
      className={cn(
        "border-border bg-card flex flex-col items-center gap-3 rounded-xl border p-8 text-center",
        className,
      )}
    >
      <div
        className={cn(
          "flex size-12 items-center justify-center rounded-full",
          failed ? "bg-destructive-soft text-destructive-strong" : "bg-muted text-muted-foreground",
        )}
      >
        <Icon className="size-6" aria-hidden />
      </div>
      <h1 className="font-display text-foreground text-lg font-semibold">
        {failed ? (
          <Trans>Couldn&apos;t load this page</Trans>
        ) : (
          <Trans>You don&apos;t have access to this section</Trans>
        )}
      </h1>
      <p className="text-muted-foreground max-w-sm text-sm">
        {message ??
          (failed ? (
            <Trans>Check your connection and try again.</Trans>
          ) : (
            <Trans>Ask the clinic owner or an admin to give you access.</Trans>
          ))}
      </p>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
        {failed && onRetry ? (
          <Button size="touch" onClick={onRetry} disabled={retrying}>
            {retrying ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <RotateCw className="size-4" aria-hidden />
            )}
            <Trans>Try again</Trans>
          </Button>
        ) : null}
        {failed && !onDashboard ? (
          <Link
            to="/account/facility/$facilityId/dashboard"
            params={{ facilityId }}
            className={buttonVariants({ variant: "outline", size: "touch" })}
          >
            <Trans>Clinic dashboard</Trans>
          </Link>
        ) : null}
        {!failed ? (
          <Link
            to="/account/user/dashboard"
            className={buttonVariants({ variant: "outline", size: "touch" })}
          >
            <Trans>Go to my account</Trans>
          </Link>
        ) : null}
      </div>
    </div>
  );
}

/** 403/404 → no access; everything else (network, 5xx) → load failed. */
export function facilityErrorKind(err: unknown): "no-access" | "load-failed" {
  const status = (err as { status?: unknown } | null)?.status;
  return status === 403 || status === 404 ? "no-access" : "load-failed";
}
